import { components } from "@appretio/api";
import { MultiSelect } from "@appretio/ui-kit";
import { useMemo } from "react";
import { Option } from "../../ui-kit/src/select/common/SelectBase";
import { ContextField, contextOptions } from "./contextOptionsConfig";

export type ContextSelectedOptions =
  components["schemas"]["ContextOptionsInput"]["filters"];

interface ContextDropdownProps {
  /**
   * Which context field this dropdown is for
   */
  field: ContextField;
  /**
   * List of available options for this context field
   */
  options: string[];
  /**
   * List of filtered options for this context field
   */
  filteredOptions: string[];
  /**
   * Loading state
   */
  isLoading: boolean;
  /**
   * Currently selected options for this context field
   */
  selected: string[];
  /**
   * Sets selected options for this context field
   * @param field - The context field
   * @param value - The selected options
   */
  setSelected: (field: ContextField, value: string[]) => void;
}

export function ContextDropdown({
  field,
  options,
  filteredOptions,
  isLoading,
  selected,
  setSelected
}: ContextDropdownProps) {
  const opts: Option[] = useMemo(() => {
    // If no filtered options, return all as relevant
    if (filteredOptions.length === 0) {
      return options.map((item) => ({
        label: item,
        value: item,
        relevant: true
      }));
    }

    return [
      ...filteredOptions.map((item) => ({
        label: item,
        value: item,
        relevant: true
      })),
      ...options
        .filter((item) => !filteredOptions.includes(item))
        .map((item) => ({ label: item, value: item, relevant: false }))
    ];
  }, [options, filteredOptions]);

  return (
    <MultiSelect
      loading={isLoading}
      placeholder={contextOptions[field].displayName}
      options={opts}
      value={selected}
      onChange={(value: string[]) => {
        setSelected(field, value);
      }}
    />
  );
}
